<template>
  <v-container
    class="report-dashboard open-shifts-dashboard py-0"
    fluid
  >
    <DashboardFilters @input="setCriteria" />
    <v-progress-linear
      :active="loading"
      color="secondary"
      indeterminate
    />
    <div
      v-if="dashboard"
      class="dashboard-content"
      :style="{ height: contentHeight }"
    >
      <div class="section-title">
        {{ $t('labels.coverage') }}
      </div>
      <v-row dense>
        <v-col
          v-for="tile in coverageTiles"
          :key="tile.key"
          cols="6"
          md="3"
        >
          <DashboardTile
            :class="tile.key"
            :detail="tile.detail"
            :label="tile.label"
            :value="tile.value"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col
          cols="12"
          md="8"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.openingsPerDay') }}
            </div>
            <BarChart
              :chart-data="openingsPerDayData"
              :options="stackedBarOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="chart-card origins"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.openingsByOrigin') }}
            </div>
            <DoughnutChart
              v-if="dashboard.openings > 0"
              :chart-data="originsData"
              :options="doughnutOptions"
              :styles="chartStyles"
            />
            <div
              v-else
              class="no-data grey--text"
              :style="chartStyles"
            >
              {{ $t('labels.noRecords') }}
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-card
            class="chart-card"
            outlined
          >
            <div class="d-flex align-center">
              <div class="chart-title">
                {{ $t('labels.coverageBy') }}
              </div>
              <v-spacer />
              <v-btn-toggle
                v-model="breakdown"
                class="breakdown"
                color="secondary"
                dense
                mandatory
              >
                <v-btn
                  v-for="option in breakdownOptions"
                  :key="option.value"
                  small
                  text
                  :value="option.value"
                >
                  {{ option.text }}
                </v-btn>
              </v-btn-toggle>
            </div>
            <HorizontalBarChart
              :chart-data="breakdownData"
              :options="horizontalStackedBarOptions"
              :styles="barChartStyles(breakdownRows.length)"
            />
          </v-card>
        </v-col>
      </v-row>

      <div class="section-title">
        {{ $t('labels.speed') }}
      </div>
      <v-row dense>
        <v-col
          v-for="tile in speedTiles"
          :key="tile.key"
          cols="6"
          md="3"
        >
          <DashboardTile
            :class="tile.key"
            :detail="tile.detail"
            :label="tile.label"
            :value="tile.value"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.medianTimeToFillByMethod') }}
            </div>
            <BarChart
              :chart-data="fillMethodsData"
              :options="barOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.coverageByPostingLeadTime') }}
            </div>
            <BarChart
              :chart-data="leadTimeData"
              :options="stackedBarOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
      </v-row>

      <div class="section-title">
        {{ $t('labels.demand') }}
      </div>
      <v-row dense>
        <v-col
          v-for="tile in demandTiles"
          :key="tile.key"
          cols="6"
          md="3"
        >
          <DashboardTile
            :class="tile.key"
            :detail="tile.detail"
            :label="tile.label"
            :value="tile.value"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.bidsPerOpenShift') }}
            </div>
            <BarChart
              :chart-data="bidsDistributionData"
              :options="barOptions"
              :styles="chartStyles"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.mostPickedUp') }}
            </div>
            <v-simple-table
              class="top-pickups"
              dense
            >
              <thead>
                <tr>
                  <th>{{ $t('labels.employeeName') }}</th>
                  <th>{{ $t('labels.homeDepartment') }}</th>
                  <th class="text-right">
                    {{ $t('labels.shifts') }}
                  </th>
                  <th class="text-right">
                    {{ $t('labels.hours') }}
                  </th>
                  <th class="text-right">
                    {{ $t('labels.bids') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in dashboard.topPickups"
                  :key="row.userId"
                >
                  <td>{{ row.name }}</td>
                  <td>{{ row.homeDepartmentName }}</td>
                  <td class="text-right">
                    {{ formatNumber(row.shifts) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.hours) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.bids) }}
                  </td>
                </tr>
                <tr v-if="dashboard.topPickups.length === 0">
                  <td
                    class="grey--text"
                    colspan="5"
                  >
                    {{ $t('labels.noPickups') }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            class="chart-card"
            outlined
          >
            <div class="chart-title">
              {{ $t('labels.mostBids') }}
            </div>
            <v-simple-table
              class="top-bidders"
              dense
            >
              <thead>
                <tr>
                  <th>{{ $t('labels.employeeName') }}</th>
                  <th>{{ $t('labels.homeDepartment') }}</th>
                  <th class="text-right">
                    {{ $t('labels.bids') }}
                  </th>
                  <th class="text-right">
                    {{ $t('labels.pickedUp') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in dashboard.topBidders"
                  :key="row.userId"
                >
                  <td>{{ row.name }}</td>
                  <td>{{ row.homeDepartmentName }}</td>
                  <td class="text-right">
                    {{ formatNumber(row.bids) }}
                  </td>
                  <td class="text-right">
                    {{ formatNumber(row.pickedUp) }}
                  </td>
                </tr>
                <tr v-if="dashboard.topBidders.length === 0">
                  <td
                    class="grey--text"
                    colspan="4"
                  >
                    {{ $t('labels.noBids') }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { BarChart, DoughnutChart, HorizontalBarChart } from '@/components/charts';
import DashboardFilters from '@/components/scheduling/reports/DashboardFilters';
import DashboardTile from '@/components/scheduling/reports/DashboardTile';
import dashboard, { LEAD_TIME_LABELS } from '@/components/scheduling/reports/dashboard';

// Labels of the API's bid count buckets, in order.
const BID_LABELS = {
  none: 'bidsNone',
  one: 'bidsOne',
  two_to_three: 'bids2To3',
  four_to_five: 'bids4To5',
  six_plus: 'bids6Plus'
};

export default {
  components: {
    BarChart,
    DashboardFilters,
    DashboardTile,
    DoughnutChart,
    HorizontalBarChart
  },
  mixins: [dashboard],
  data () {
    return {
      breakdown: this.$store.getters['account/isDepartmentManagement']() ? 'shiftType' : 'department',
      retrieveAction: 'report/retrieveOpenShiftsDashboard',
      retrieveFailMessage: 'descriptions.openShiftsDashboardRetrieveFail'
    };
  },
  computed: {
    barOptions () {
      return {
        ...this.groupedBarOptions,
        legend: {
          display: false
        }
      };
    },
    bidsDistributionData () {
      const rows = this.dashboard.bids.distribution;
      return {
        labels: rows.map(row => this.$t(`labels.${BID_LABELS[row.bucket]}`)),
        datasets: [this.dataset(this.$tc('labels.openShift', 2), this.colors.secondary, rows.map(row => row.count))]
      };
    },
    breakdownData () {
      return this.coverageData(this.breakdownRows.map(row => ({ ...row, name: row.name || this.$t('labels.unknown') })));
    },
    breakdownOptions () {
      const options = [];
      if (!this.isDepartmentManagement) {
        options.push({ text: this.$tc('labels.department', 1), value: 'department' });
      }
      options.push(
        { text: this.$t('labels.shiftType'), value: 'shiftType' },
        { text: this.$t('labels.day'), value: 'weekday' }
      );
      return options;
    },
    breakdownRows () {
      switch (this.breakdown) {
        case 'shiftType':
          return this.dashboard.byShiftType;
        case 'weekday':
          return this.weekdayRows(this.dashboard.byWeekday);
        default:
          return this.dashboard.byDepartment;
      }
    },
    coverageTiles () {
      const { filled, fillRate, openShifts, openings, stillOpen, wentUnfilled } = this.dashboard;
      return [
        {
          key: 'openings',
          label: this.$t('labels.openings'),
          value: this.formatNumber(openings),
          detail: this.$tc('labels.openShiftCount', openShifts, { count: this.formatNumber(openShifts) })
        },
        {
          key: 'filled',
          label: this.$t('labels.filled'),
          value: this.formatNumber(filled),
          detail: fillRate === null ? '' : this.$t('labels.fillRateDetail', { percent: this.formatPercent(fillRate) })
        },
        {
          key: 'still-open',
          label: this.$t('labels.stillOpen'),
          value: this.formatNumber(stillOpen),
          detail: this.$t('labels.stillOpenDetail')
        },
        {
          key: 'went-unfilled',
          label: this.$t('labels.wentUnfilled'),
          value: this.formatNumber(wentUnfilled),
          detail: this.$t('labels.wentUnfilledDetail')
        }
      ];
    },
    demandTiles () {
      const { bids, openShifts } = this.dashboard;
      return [
        {
          key: 'bids',
          label: this.$t('labels.bids'),
          value: this.formatNumber(bids.total),
          detail: bids.perOpening === null ? '' : this.$t('labels.perOpening', { count: this.formatNumber(bids.perOpening) })
        },
        {
          key: 'no-bids',
          label: this.$t('labels.noBidsTile'),
          value: this.formatNumber(bids.noBids),
          detail: openShifts > 0 ? this.$t('labels.ofOpenShifts', { percent: this.formatPercent(bids.noBidsPercent) }) : ''
        }
      ];
    },
    fillMethodsData () {
      const rows = this.dashboard.timeToFill.byMethod;
      return {
        labels: rows.map(row => this.$t(row.method === 'fcfs' ? 'labels.firstComeFirstServed' : 'labels.managerSelected')),
        datasets: [{
          backgroundColor: rows.map(row => (row.method === 'fcfs' ? this.colors.secondary : this.colors.primary)),
          data: rows.map(row => row.medianHours),
          label: this.$t('labels.medianTimeToFill')
        }]
      };
    },
    leadTimeData () {
      return this.coverageData(this.dashboard.leadTime.map(row => ({
        ...row,
        name: this.$t(`labels.${LEAD_TIME_LABELS[row.bucket]}`)
      })));
    },
    openingsPerDayData () {
      const { byDate } = this.dashboard;
      return {
        labels: this.dayLabels(byDate),
        datasets: [
          this.dataset(this.$t('labels.filled'), this.colors.success, byDate.map(day => day.filled)),
          this.dataset(this.$t('labels.unfilled'), this.colors.error, byDate.map(day => day.unfilled))
        ]
      };
    },
    originsData () {
      const rows = this.dashboard.byOpeningType;
      const colors = { giveaway: this.colors.accent, shift: this.colors.secondary, swap: this.colors.primary };
      const labels = {
        giveaway: this.$t('labels.giveaway'),
        shift: this.$t('labels.postedShift'),
        swap: this.$t('labels.swap')
      };
      return {
        labels: rows.map(row => labels[row.type] || row.type),
        datasets: [{
          backgroundColor: rows.map(row => colors[row.type] || '#9E9E9E'),
          data: rows.map(row => row.openings)
        }]
      };
    },
    speedTiles () {
      const { timeToFill } = this.dashboard;
      return [
        {
          key: 'time-to-fill',
          label: this.$t('labels.medianTimeToFill'),
          value: this.formatHours(timeToFill.medianHours),
          detail: this.$t('labels.timeToFillDetail')
        },
        {
          key: 'filled-within-day',
          label: this.$t('labels.filledWithinDay'),
          value: this.formatPercent(timeToFill.withinDayPercent),
          detail: ''
        }
      ];
    }
  },
  methods: {
    // Filled and unfilled openings of rows with a name, openings and filled.
    coverageData (rows) {
      return {
        labels: rows.map(row => row.name),
        datasets: [
          this.dataset(this.$t('labels.filled'), this.colors.success, rows.map(row => row.filled || 0)),
          this.dataset(this.$t('labels.unfilled'), this.colors.error, rows.map(row => (row.openings || 0) - (row.filled || 0)))
        ]
      };
    }
  }
};
</script>

<style lang="scss">
@import "src/assets/sass/report-dashboard.scss";
</style>
